/**
 * `AutoRefundSection` — per-SITE auto-refund control (ADR-0099 Slice 2b).
 * Mounted in `SettingsIntegrations.tsx` directly below the settlement-rail
 * sections (it governs the same money-out rail the operator trusts there).
 *
 * Auto-refund RELAXES the HITL human checkpoint: when on, a refund at or
 * under the cap that is cryptographically attributable to the original
 * order's signing agent settles WITHOUT human review; anything above the
 * cap (or from a different agent) still escalates. The copy states that
 * trade-off plainly — this is a governance control, not a convenience
 * toggle.
 *
 * Role gate: owner/admin only. The section renders nothing for
 * reviewer/viewer, and the server GET omits the config for them anyway
 * (a `forbidden` load) — UI hiding is defense-in-depth, the server PUT is
 * the real gate. Turning OFF is the safe direction (optimistic, immediate).
 * Turning ON requires a positive cap and an explicit Save (a money
 * setting must not blur-save on a phone).
 */

import { useCallback, useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';

import type { UserRole } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Card } from '../../components/atoms/index.js';
import { getSiteAutoRefund, putSiteAutoRefund } from '../../api/site-auto-refund.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

/** Matches the server ceiling (AUTO_REFUND_MAX_CEILING_MINOR = 100_000_000). */
const MAX_CAP_MAJOR = 1_000_000;

export interface AutoRefundSectionProps {
  readonly site_id: string;
  readonly role_override?: UserRole;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; enabled: boolean; max_minor: number | null }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

/** Validate the cap input (major-unit string) → minor units, or an error. */
function parseCap(raw: string): { minor: number } | { error: string } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { error: 'Enter a cap to enable auto-approval.' };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return { error: 'Enter a positive amount (max 2 decimals).' };
  }
  const major = Number(trimmed);
  if (!Number.isFinite(major) || major <= 0) return { error: 'Enter a positive amount.' };
  if (major > MAX_CAP_MAJOR) return { error: 'Maximum is $1,000,000.00.' };
  return { minor: Math.round(major * 100) };
}

function minorToMajorStr(minor: number | null): string {
  if (minor === null) return '';
  return (minor / 100).toFixed(2);
}

export function AutoRefundSection({
  site_id,
  role_override,
}: AutoRefundSectionProps): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const toast = useToast();

  const role: UserRole | null =
    role_override !== undefined
      ? role_override
      : auth !== null && auth.state.kind === 'authenticated'
        ? auth.state.user.role
        : null;
  const can_read = role === 'owner' || role === 'admin';

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [capStr, setCapStr] = useState('');
  const [capError, setCapError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!can_read) {
      setState({ kind: 'forbidden' });
      return;
    }
    if (http === null) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      const result = await getSiteAutoRefund(http, site_id);
      if (cancelled) return;
      switch (result.kind) {
        case 'ok':
          setState({ kind: 'ready', enabled: result.enabled, max_minor: result.max_minor });
          setCapStr(minorToMajorStr(result.max_minor));
          break;
        case 'forbidden':
          setState({ kind: 'forbidden' });
          break;
        case 'not_found':
          setState({ kind: 'error', message: 'Site not found.' });
          break;
        case 'error':
          setState({ kind: 'error', message: result.message });
          break;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [http, can_read, site_id]);

  const applyResult = useCallback(
    (
      result: Awaited<ReturnType<typeof putSiteAutoRefund>>,
      rollback: () => void,
      successBody: string,
    ): void => {
      switch (result.kind) {
        case 'ok':
          setState({ kind: 'ready', enabled: result.enabled, max_minor: result.max_minor });
          setCapStr(minorToMajorStr(result.max_minor));
          setCapError(null);
          toast({ tone: 'lime', title: 'Auto-refund saved.', body: successBody });
          break;
        case 'cap_required':
          rollback();
          setCapError('Enter a cap to enable auto-approval.');
          break;
        case 'invalid_max':
          rollback();
          setCapError('That cap is not valid.');
          break;
        case 'forbidden':
          rollback();
          toast({ tone: 'coral', title: 'Your role cannot change this setting.' });
          break;
        case 'not_found':
          rollback();
          toast({ tone: 'coral', title: 'Site not found.' });
          break;
        case 'error':
          rollback();
          toast({ tone: 'coral', title: 'Could not save auto-refund.', body: result.message });
          break;
      }
    },
    [toast],
  );

  // Turning OFF — the safe direction; optimistic + immediate (the cap is
  // preserved server-side via COALESCE and re-shown on re-enable).
  const disable = useCallback(async (): Promise<void> => {
    if (http === null || state.kind !== 'ready' || pending) return;
    const prev = state;
    setPending(true);
    setState({ kind: 'ready', enabled: false, max_minor: state.max_minor });
    const result = await putSiteAutoRefund(http, site_id, { enabled: false });
    setPending(false);
    applyResult(result, () => setState(prev), 'Auto-approval is off — every refund goes to a person.');
  }, [http, state, pending, site_id, applyResult]);

  // Enabling (or re-saving the cap while on) — requires a valid cap +
  // explicit Save.
  const enableWithCap = useCallback(async (): Promise<void> => {
    if (http === null || state.kind !== 'ready' || pending) return;
    const parsed = parseCap(capStr);
    if ('error' in parsed) {
      setCapError(parsed.error);
      return;
    }
    setCapError(null);
    const prev = state;
    setPending(true);
    const result = await putSiteAutoRefund(http, site_id, { enabled: true, max_minor: parsed.minor });
    setPending(false);
    applyResult(
      result,
      () => setState(prev),
      `Refunds up to $${(parsed.minor / 100).toFixed(2)} are now auto-approved.`,
    );
  }, [http, state, pending, capStr, site_id, applyResult]);

  if (!can_read || state.kind === 'forbidden') return null;

  const enabled = state.kind === 'ready' ? state.enabled : false;

  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-5 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
          AUTO-REFUND
        </div>
        <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text, letterSpacing: -0.5, marginBottom: 4 }}>
          Approve refunds under a cap automatically
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          When on, refunds at or below the cap — from the same agent that placed
          the order — are approved without human review. Refunds above the cap,
          or from a different agent, still escalate to you.
        </div>
      </div>

      {state.kind === 'loading' ? (
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
          Reading auto-refund…
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="mt-2" style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5 }}>
          Couldn&apos;t load auto-refund — {state.message}
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <div className="space-y-4">
          {/* Toggle row */}
          <div
            className="flex items-center justify-between gap-3 p-3"
            style={{ border: `1px solid ${C.border}`, borderRadius: 2 }}
          >
            <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
              Auto-approve refunds under cap
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label="Auto-approve refunds under cap"
              disabled={pending}
              onClick={() => {
                if (enabled) void disable();
                else {
                  // Reveal the cap input; enabling commits via Save.
                  setState({ kind: 'ready', enabled: true, max_minor: state.max_minor });
                }
              }}
              style={{
                width: 44,
                height: 26,
                minWidth: 44,
                borderRadius: 13,
                border: `1px solid ${enabled ? C.lime : C.border}`,
                background: enabled ? C.lime : C.surface2,
                position: 'relative',
                cursor: pending ? 'not-allowed' : 'pointer',
                transition: 'background 120ms, border-color 120ms',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 2,
                  left: enabled ? 20 : 2,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: enabled ? C.ink : C.muted,
                  transition: 'left 120ms',
                }}
              />
            </button>
          </div>

          {/* Cap input — revealed only when on (a cap with auto-refund off
              is noise; the last cap is retained server-side). */}
          {enabled ? (
            <div>
              <label
                htmlFor="auto-refund-cap"
                style={{ display: 'block', fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}
              >
                MAXIMUM AUTO-APPROVE AMOUNT
              </label>
              <div className="flex items-center gap-2">
                <div
                  className="flex items-center"
                  style={{
                    border: `1px solid ${capError !== null ? C.coralFg : C.border}`,
                    borderRadius: 2,
                    background: C.surface2,
                    overflow: 'hidden',
                  }}
                >
                  <span style={{ fontFamily: monoFont, fontSize: 12, color: C.muted, padding: '8px 4px 8px 10px' }}>
                    $
                  </span>
                  <input
                    id="auto-refund-cap"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    value={capStr}
                    disabled={pending}
                    onChange={(e) => {
                      setCapStr(e.target.value);
                      if (capError !== null) setCapError(null);
                    }}
                    placeholder="50.00"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      color: C.text,
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      padding: '8px 10px 8px 0',
                      width: 120,
                    }}
                  />
                </div>
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => void enableWithCap()}
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    letterSpacing: 1,
                    color: C.ink,
                    background: C.lime,
                    border: 'none',
                    borderRadius: 2,
                    padding: '8px 14px',
                    minHeight: 36,
                    cursor: pending ? 'not-allowed' : 'pointer',
                  }}
                >
                  {pending ? 'SAVING…' : 'SAVE'}
                </button>
              </div>
              {capError !== null ? (
                <div className="mt-1" style={{ fontFamily: sansFont, fontSize: 11, color: C.coralFg, lineHeight: 1.4 }}>
                  {capError}
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-1" style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.4 }}>
                  <RotateCcw size={11} color={C.muted} />
                  Refunds at or below this amount are approved automatically. Anything higher still goes to a person.
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
